import { useState } from "react";
import { Instagram, Twitter, Facebook, ArrowRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const FALLBACK_COLS = [
  { title: "Shop", links: [
    { label: "Bathroom Tissue", to: "/shop?category=bathroom-tissue" },
    { label: "Kitchen Towels", to: "/shop?category=kitchen-towel" },
  ]},
  { title: "Company", links: [{ label: "About", to: "/about" }, { label: "Contact", to: "/contact" }] },
  { title: "Support", links: [{ label: "FAQ", to: "/#faq" }, { label: "Contact Us", to: "/contact" }] },
];

export const Footer = () => {
  const { content } = useContent();
  const cols = content?.footer_columns?.length ? content.footer_columns : FALLBACK_COLS;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Morning Soft family!", { description: "Check your inbox for a sunny surprise." });
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-10 bg-[#0a1936] text-white pt-32 pb-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              Start your day
              <br />
              <span className="text-amber-400">in comfort.</span>
            </h2>
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-sky-200/70 mb-6 leading-relaxed">
              Fresh drops, family deals, and sunny household tips — straight to your inbox. No spills, no spam.
            </p>
            <form onSubmit={subscribe} noValidate className="flex items-center gap-3 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full bg-white/10 border border-white/20 px-6 py-4 text-white placeholder:text-sky-300/50 focus:outline-none focus:border-amber-400 transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="shrink-0 flex items-center justify-center w-14 h-14 rounded-full bg-amber-400 text-slate-900 hover:bg-white hover:scale-105 transition-[background-color,transform] duration-300 disabled:opacity-60"
                aria-label="Subscribe"
              >
                <ArrowRight size={20} strokeWidth={2.5} />
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-16 border-b border-white/10">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4" data-testid="footer-logo">
              <img src="/products/img_10.png" alt="Morning Soft" className="h-14 w-14 object-contain bg-white/10 rounded-full p-1" />
              <div>
                <p className="font-display font-extrabold text-xl tracking-tight">
                  Morning<span className="text-amber-400">Soft</span><sup className="text-xs ml-0.5">®</sup>
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-sky-300/70 mt-0.5">Everyday Premium™</p>
              </div>
            </Link>
            <p className="text-sm text-sky-200/60 leading-relaxed max-w-xs">
              Softness you can feel. Quality you can trust. A U.S.-based household paper &amp; personal-care brand building the future of everyday comfort.
            </p>
            <a href="mailto:sales@morningsofttissue.com" className="mt-4 inline-flex items-center gap-2 text-sm text-sky-200/80 hover:text-amber-400 transition-colors">
              <Mail size={14} /> sales@morningsofttissue.com
            </a>
            <div className="flex gap-3 mt-6">
              {[Instagram, Twitter, Facebook].map((Icon, i) => (
                <button
                  key={i}
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-amber-400 hover:text-slate-900 transition-colors duration-300"
                  aria-label="Social link"
                >
                  <Icon size={16} />
                </button>
              ))}
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="font-display font-bold text-sm uppercase tracking-widest text-amber-400 mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-sm text-sky-100/70 hover:text-amber-400 transition-colors duration-200"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 text-xs text-sky-200/50">
          <p>© {new Date().getFullYear()} Morning Soft LLC · Angleton, Texas 77515</p>
          <p>www.morningsofttissue.com · Start Your Day in Comfort.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
